import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { Folder } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faPenToSquare, faTrashCan, faFolder, faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import FolderCreateModal from '@/Pages/Dashboard/Folders/FolderCreateModal';
import FolderRenameModal from '@/Pages/Dashboard/Folders/FolderRenameModal';
import Dropdown from '@/Components/Dropdown';

interface FolderSidebarProps {
    folders: Folder[];
    selectedFolderId: string | null;
    onMoveToFolder: (jobIds: number[], folderId: string | null) => void;
    dragOverFolder: string | null;
    isDragging: boolean;
    onFolderDragOver: (e: React.DragEvent, folderId: string | null) => void;
    onFolderDragLeave: () => void;
    onFolderDrop: (e: React.DragEvent, folderId: string | null) => void;
    onFolderDelete: (folder: Folder) => void;
    onFolderCreated: (folder: Folder) => void;
    onFolderRenamed: (folder: Folder) => void;
}

export default function FolderSidebar({
    folders,
    selectedFolderId,
    onMoveToFolder,
    dragOverFolder,
    isDragging,
    onFolderDragOver,
    onFolderDragLeave,
    onFolderDrop,
    onFolderDelete,
    onFolderCreated,
    onFolderRenamed
}: FolderSidebarProps) {
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

    const handleFolderClick = (folderId: string | null) => {
        const params = new URLSearchParams(window.location.search);
        if (folderId === null) {
            router.visit(route('dashboard'))
        } else {
            router.visit(route('dashboard.folder', folderId))
        }
    };

    const openRenameModal = (folder: Folder) => {
        setSelectedFolder(folder);
        setShowRenameFolderModal(true);
    };

    const openDeleteModal = (folder: Folder) => {
        onFolderDelete(folder);
    };

    return (
        <>
            <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                <h6 className="mb-3 mt-1 text-lg  font-semibold text-black dark:text-gray-100">Folders</h6>
                <div className="space-y-1">
                    {[null, ...folders].map((folder) => (
                        <div key={folder?.id || 'all'} className={`relative group rounded  transition-colors ${
                            selectedFolderId == folder?.id
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } ${isDragging && dragOverFolder == folder?.id ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}>
                            <button
                                onClick={() => handleFolderClick(folder?.id || null)}
                                onDragOver={(e) => onFolderDragOver(e, folder?.id || null)}
                                onDragLeave={onFolderDragLeave}
                                onDrop={(e) => onFolderDrop(e, folder?.id || null)}
                                className="w-full text-left px-3 py-2 flex items-center"
                            >
                                <FontAwesomeIcon
                                    icon={selectedFolderId == folder?.id ? faFolderOpen : faFolder}
                                    className="mr-2 text-gray-500 dark:text-gray-400"
                                />
                                {folder?.name ? <span className="truncate notranslate">{folder.name}</span> : <span className="truncate">All Files</span>}
                            </button>
                            {
                                folder && (
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                        <Dropdown>
                                            <Dropdown.Trigger>
                                                {(isOpen) => (
                                                    <button
                                                        type="button"
                                                        className={`w-6 h-6 flex items-center justify-center text-gray-500 dark:text-gray-400 rounded transition-all duration-200 ${
                                                            isOpen
                                                                ? 'bg-gray-100 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-600 opacity-100'
                                                                : 'bg-gray-100 dark:bg-gray-700 hover:bg-white dark:hover:bg-gray-600 opacity-0 group-hover:opacity-100'
                                                        }`}
                                                    >
                                                        <FontAwesomeIcon icon={faEllipsisH} className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </Dropdown.Trigger>
                                            <Dropdown.Content align="right">
                                                <Dropdown.Item
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openRenameModal(folder);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} className="w-3 h-3 mr-2" />
                                                    Rename
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteModal(folder);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} className="w-3 h-3 mr-2" />
                                                    Delete
                                                </Dropdown.Item>
                                            </Dropdown.Content>
                                        </Dropdown>
                                    </div>
                                )
                            }
                        </div>
                    ))}
                    <button
                        className="w-full text-left px-3 py-1.5 rounded transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                        onClick={() => setShowCreateFolderModal(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5 mr-1.5 text-gray-500 dark:text-gray-400 inline-block">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                        </svg>

                        New Folder
                    </button>
                </div>
            </div>

            <FolderCreateModal
                show={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                onFolderCreated={onFolderCreated}
            />
            <FolderRenameModal
                show={showRenameFolderModal}
                folder={selectedFolder}
                onClose={() => {
                    setShowRenameFolderModal(false);
                    setSelectedFolder(null);
                }}
                onFolderRenamed={onFolderRenamed}
            />
        </>
    );
}
